import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const siteUrl = 'https://aexionstudios.com.br'
const defaultDescription = 'Produtos digitais, inteligência artificial, games e experiências interativas construídas com propósito.'
type Metadata = { title: string; description: string; noindex?: boolean }

const pages: Record<string, Metadata> = {
  '/': { title: 'AEXION Studios — Tecnologia, games e novos universos', description: defaultDescription },
  '/produtos': { title: 'Produtos digitais | AEXION Studios', description: 'Conheça produtos, jogos e experiências digitais desenvolvidos pela AEXION Studios.' },
  '/demos': { title: 'Demonstrações interativas | AEXION Studios', description: 'Experimente protótipos de software, inteligência artificial e experiências digitais da AEXION.' },
  '/agroaprendiz': { title: 'AgroAprendiz — Educação gamificada | AEXION Studios', description: 'Conheça o AgroAprendiz, uma experiência educacional gamificada sobre agricultura, sustentabilidade, gestão e cooperativismo.' },
  '/galaxy': { title: 'Galaxy — Roguelite espacial | AEXION Studios', description: 'Explore o universo de Galaxy, um roguelite espacial com combate, evolução de nave e bosses planetários.' },
  '/projetos': { title: 'Projetos e universos | AEXION Studios', description: 'Explore AgroAprendiz, Galaxy e os laboratórios de tecnologia e experiências interativas da AEXION.' },
  '/solucoes': { title: 'Software, IA e gamificação | AEXION Studios', description: 'Soluções em desenvolvimento de software, inteligência artificial, games, gamificação e experiências digitais.' },
  '/sobre': { title: 'Sobre a AEXION Studios', description: 'Conheça a AEXION, estúdio brasileiro que une criatividade, engenharia e visão de produto para criar novos universos.' },
  '/contato': { title: 'Fale com a AEXION Studios', description: 'Converse com a AEXION sobre software, inteligência artificial, games, parcerias e novos projetos.' },
  '/privacidade': { title: 'Política de Privacidade | AEXION Studios', description: 'Saiba como a AEXION trata dados pessoais e protege a privacidade de visitantes e contatos.' },
  '/termos': { title: 'Termos de Uso | AEXION Studios', description: 'Consulte os termos de uso das plataformas e experiências digitais da AEXION Studios.' },
  '/admin': { title: 'Administração | AEXION Studios', description: 'Área administrativa restrita.', noindex: true },
}

function upsertMeta(selector: string, attributes: Record<string, string>, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value))
    document.head.append(element)
  }
  element.content = content
}

function routeMetadata(pathname: string): Metadata {
  const exactPage = pages[pathname]
  if (exactPage) return exactPage
  if (pathname.startsWith('/produto/')) return { title: 'Produto | AEXION Studios', description: 'Conheça este produto digital desenvolvido pela AEXION Studios.' }
  if (pathname.startsWith('/demo/')) return { title: 'Demonstração | AEXION Studios', description: 'Teste esta demonstração interativa desenvolvida pela AEXION Studios.' }
  if (pathname.startsWith('/admin/')) return { title: 'Administração | AEXION Studios', description: 'Área administrativa restrita.', noindex: true }
  return { title: 'Página não encontrada | AEXION Studios', description: defaultDescription, noindex: true }
}

export function SeoManager() {
  const { pathname } = useLocation()

  useEffect(() => {
    const metadata = routeMetadata(pathname)
    const canonicalUrl = `${siteUrl}${pathname === '/' ? '' : pathname}`
    document.title = metadata.title
    upsertMeta('meta[name="description"]', { name: 'description' }, metadata.description)
    upsertMeta('meta[name="robots"]', { name: 'robots' }, metadata.noindex ? 'noindex, nofollow' : 'index, follow')
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, metadata.title)
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, metadata.description)
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl)
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, metadata.title)
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, metadata.description)
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }
    canonical.href = canonicalUrl

    let structuredData = document.head.querySelector<HTMLScriptElement>('#aexion-structured-data')
    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'aexion-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.append(structuredData)
    }
    structuredData.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': pathname === '/' ? 'Organization' : 'WebPage',
      name: metadata.title,
      url: canonicalUrl,
      description: metadata.description,
      ...(pathname === '/' ? { email: 'contato@aexionstudios.com.br', brand: 'AEXION Studios' } : {}),
    })
  }, [pathname])

  return null
}
